import { useTranslation } from 'react-i18next';
import { getConceptTypeContent, resolveConceptLabelOrName } from 'app/utils/tag-utils';
import { TagItemShortcuts } from 'features/tagging/TagItemShortcuts/TagItemShortcuts';
import { Typography } from 'app/components/ui';
import { PaginatedTagsResponse, TagWithMetadata } from 'entities/tag';
import { useGoToTag } from 'app/utils/hooks/useGoToTag';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { formatTaxonomyName } from 'app/utils/tag-utils';

import './OverviewTable.scss';

interface OverviewTableProps {
  categories: string[];
  tableData: TagWithMetadata[];
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onNavigate?: () => void;
  pageSize?: number;
  chains?: PaginatedTagsResponse['chains'];
}

export const OverviewTable = ({ categories, tableData, onTagDetailsClick, onNavigate, pageSize, chains }: OverviewTableProps) => {
  const { t } = useTranslation(['all', 'tag']);

  const { goToTag, getGoToTagState } = useGoToTag();
  const { tagLanguage } = useTagDisplay();

  const handleGoToTag = (tag: TagWithMetadata) => {
    if (getGoToTagState(tag).disabled) return;
    onNavigate?.();
    goToTag(tag);
  };

  return (
    <table className="overview-table">
      <thead>
        <tr>
          {categories.map((category) => (
            <th key={category}>
              <Typography variant="body-7" fontWeight="semibold" color="#547ba2">
                {category}
              </Typography>
            </th>
          ))}
          <th></th>
        </tr>
      </thead>

      <tbody>
        {tableData.length > 0 ? (
          <>
            {tableData.map((tag) => {
              const label = resolveConceptLabelOrName({ concept: tag.concept, currentLang: tagLanguage });
              const { conceptLabel } = tag.concept?.conceptType ? getConceptTypeContent(tag.concept.conceptType) : { conceptLabel: '-' };
              const goToState = getGoToTagState(tag);
              const chain = chains?.[tag.id];
              const status = chain?.status ?? tag.status;
              const validationStatus = chain?.validationStatus ?? tag.validationStatus;
              const page =
                chain?.firstPage != null && chain.lastPage != null && chain.firstPage !== chain.lastPage
                  ? `${chain.firstPage}–${chain.lastPage}`
                  : tag.metadata?.pageNumber ?? '-';
              return (
                <tr
                  key={tag.id}
                  className={`overview-table__row${goToState.disabled ? ' overview-table__row--disabled' : ''}`}
                  onClick={() => handleGoToTag(tag)}
                >
                  <td>
                    <Typography variant="body-7" fontWeight="light">
                      {label}
                    </Typography>
                    {chain && (
                      <div className="overview-table__chain">
                        <i className="fa-solid fa-link" />
                        <Typography variant="body-3" fontWeight="semibold">
                          {t('tag:overview.chain.parts', { count: chain.parts })}
                        </Typography>
                      </div>
                    )}
                  </td>
                  <td>
                    <Typography variant="body-7" fontWeight="light">
                      {conceptLabel}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body-7" fontWeight="light">
                      {tag.taxonomy?.name ? formatTaxonomyName(tag.taxonomy.name) : '-'}
                    </Typography>
                  </td>
                  <td>
                    <div className={`overview-table__tag-status overview-table__tag-status--${status}`}>
                      <Typography variant="body-3" fontWeight="semibold">
                        {status ? t(`tag:status.${status}`) : '-'}
                      </Typography>
                    </div>
                  </td>
                  <td>
                    <div className={`overview-table__tag-status overview-table__tag-status--${validationStatus}`}>
                      <Typography variant="body-3" fontWeight="semibold">
                        {validationStatus ? t(`tag:status.${validationStatus}`) : '-'}
                      </Typography>
                    </div>
                  </td>
                  <td className="overview-table__icon-btn">{tag.concept?.isMandatory ? <i className="fa-solid fa-check" /> : null}</td>
                  <td>
                    <div className={`overview-table__origin overview-table__origin--${tag.origin ?? 'human'}`}>
                      <Typography variant="body-3" fontWeight="semibold">
                        {t(`tag:ai.origin.${tag.origin ?? 'human'}`)}
                      </Typography>
                    </div>
                  </td>
                  <td className="overview-table__page">
                    <Typography variant="body-7" fontWeight="light">
                      {page}
                    </Typography>
                  </td>
                  <td>
                    <TagItemShortcuts
                      actions={[
                        ...(onTagDetailsClick ? [{ type: 'details' as const, onClick: () => onTagDetailsClick(tag) }] : []),
                        {
                          type: 'navigate' as const,
                          disabled: goToState.disabled,
                          tooltip: goToState.tooltip,
                          onClick: () => handleGoToTag(tag),
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
            {pageSize &&
              tableData.length < pageSize &&
              Array.from({ length: pageSize - tableData.length }, (_, i) => (
                <tr key={`empty-${i}`} className="overview-table__empty-row">
                  <td colSpan={categories.length + 1}>&nbsp;</td>
                </tr>
              ))}
          </>
        ) : (
          <tr>
            <td colSpan={categories.length + 1}>{t('all:tags.empty.list')}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
